import * as React from "react";
import styled from "styled-components";
import {withAppStores} from "../../../../../AppStores";
import type {IBasicPropsFor3DTweaking} from "../../../../modules/space/SpaceView";
import type {AppState} from "../../../../../data/state/AppState";
import {HorizontalAlignment} from "../../../../../utils/dom/DomUtils";
import {SVGIcon} from "../../../../widgets/button/SVGIcon";
import {Permission, XyiconFeature} from "../../../../../generated/api/base";
import type {Type} from "../../../../../data/models/Type";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {ReactUtils} from "../../../../utils/ReactUtils";
import {DomPortal} from "../../../../modules/abstract/portal/DomPortal";
import {colorPalette} from "../../../styles/colorPalette";
import {baseDistance, ELLIPSIS, FLEXCENTER, fontSize, radius, VerticalFlexStyle} from "../../../styles/styles";
import {InfoBubbleV5Styled} from "../../../spaceeditor/SpaceItemV5";

interface IBoundaryTypeContainerProps extends IBasicPropsFor3DTweaking {
	readonly onBoundaryTypeClick: (typeId: string) => void;
	readonly appState?: AppState;
}

interface IBoundaryTypeContainerState {
	isToolTipOpen: boolean;
	content: React.ReactNode;
}

@withAppStores("appState")
export class BoundaryTypeContainerV5 extends React.Component<IBoundaryTypeContainerProps, IBoundaryTypeContainerState> {
	private _parents: HTMLDivElement[] = [];
	private _timeOutId: number = null;
	private _currentIndex: number = -1;

	constructor(props: IBoundaryTypeContainerProps) {
		super(props);
		this.state = {
			isToolTipOpen: false,
			content: "",
		};
	}

	private openTooltip = (event: React.MouseEvent, hasPermission: boolean, index: number, name: string) => {
		const isOverflowing = event.currentTarget.scrollWidth > event.currentTarget.clientWidth;

		if (!hasPermission || isOverflowing) {
			if (this._timeOutId) {
				clearTimeout(this._timeOutId);
			}

			this._timeOutId = window.setTimeout(() => {
				this._currentIndex = index;
				this.setState({
					isToolTipOpen: true,
					content: !hasPermission ? (
						<>
							<SVGIcon icon="locked" />
							<div>You do not have permission to use this boundary.</div>
						</>
					) : (
						name
					),
				});
			}, 1000);
		}
	};

	private closeTooltip = () => {
		clearTimeout(this._timeOutId);

		this.setState({
			isToolTipOpen: false,
		});
	};

	private getBoundaryList = () => {
		const {actions} = this.props.appState;

		return actions
			.getTypesByFeature(XyiconFeature.Boundary)
			.slice()
			.filter((a: Type) => actions.getModuleTypePermission(a.id, a.feature) !== Permission.None)
			.sort((a: Type, b: Type) => {
				const aHasPermission = actions.getModuleTypePermission(a.id, a.feature) >= Permission.Update;
				const bHasPermission = actions.getModuleTypePermission(b.id, b.feature) >= Permission.Update;

				if (aHasPermission && !bHasPermission) {
					return -1;
				} else if (!aHasPermission && bHasPermission) {
					return 1;
				} else {
					return StringUtils.sortIgnoreCase(a.name, b.name);
				}
			});
	};

	private onClick = (id: string, hasPermission: boolean) => {
		if (hasPermission) {
			this.props.onBoundaryTypeClick(id);
			this.forceUpdate();
		}
	};

	private get modalContainer() {
		return this.props.appState.app.modalContainer;
	}

	public override componentWillUnmount() {
		clearTimeout(this._timeOutId);
	}

	public override render() {
		const {spaceViewRenderer} = this.props;
		const {isToolTipOpen, content} = this.state;

		const inlineStyle: React.CSSProperties = {
			zIndex: 99999,
		};

		const boundaryTypeList = this.getBoundaryList();
		const isAnyTypeNotHidden = spaceViewRenderer.actions.hasAnyTypeTheGivenPermissionInModule(XyiconFeature.Boundary, Permission.View);

		return (
			<BoundaryTypeContainerStyled data-cy="boundary-type-container">
				{isAnyTypeNotHidden ? (
					boundaryTypeList.map((type: Type, index: number) => {
						const currentTypeId = spaceViewRenderer.boundaryManager.typeId;
						const typeId = type.id;
						const currentPermission = spaceViewRenderer.actions.getModuleTypePermission(typeId, XyiconFeature.Boundary);
						const hasPermission = currentPermission >= Permission.Update;

						return (
							<BoundaryTypeRow
								ref={(divRef) => {
									this._parents[index] = divRef;
								}}
								className={ReactUtils.cls({active: typeId === currentTypeId, noPermission: !hasPermission})}
								onClick={() => this.onClick(typeId, hasPermission)}
								onMouseOver={(event) => this.openTooltip(event, hasPermission, index, type.name)}
								onMouseLeave={this.closeTooltip}
								key={type.id}
							>
								<ColorIconWrapper>
									<ColorIcon $color={type.settings.color.hex} />
								</ColorIconWrapper>
								<BoundaryTypeName>{type.name}</BoundaryTypeName>
							</BoundaryTypeRow>
						);
					})
				) : (
					<EmptyContainerStyled>There are no boundary items to display</EmptyContainerStyled>
				)}
				{isToolTipOpen && (
					<DomPortal destination={this.modalContainer}>
						<InfoBubbleV5Styled
							content={content}
							style={inlineStyle}
							anchorElement={this._parents[this._currentIndex]}
							horizontalAlignment={HorizontalAlignment.left}
							className={ReactUtils.cls("SpaceItem", {nameToolTip: typeof content === "string"})}
						/>
					</DomPortal>
				)}
			</BoundaryTypeContainerStyled>
		);
	}
}

const ColorIconWrapper = styled.div`
	min-width: 24px;
	height: 24px;
	background-color: ${colorPalette.white};
	position: relative;
	border-radius: 100%;
`;

const ColorIcon = styled.div<{$color: string}>`
	width: 100%;
	height: 100%;
	border-radius: 100%;
	border: 2px solid #${(props) => props.$color};
	background-color: #${(props) => props.$color}22;
`;

const BoundaryTypeRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	border-radius: ${radius.sm};
	min-height: 32px;
	cursor: pointer;
	gap: ${baseDistance.sm};
	padding: 0 ${baseDistance.xs};

	&:hover:not(.noPermission) {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.active {
		background-color: ${colorPalette.primary.c200Light};
		color: ${colorPalette.primary.c500Primary};
	}

	&.noPermission {
		cursor: not-allowed;
		opacity: 0.5;
		padding: 5px;
	}
`;

const BoundaryTypeName = styled.div`
	${ELLIPSIS};
`;

const BoundaryTypeContainerStyled = styled.div`
	${VerticalFlexStyle};
	overflow-y: auto;
	max-height: calc(100vh - 135px);
	gap: ${baseDistance.sm};
	padding: 0 ${baseDistance.md} ${baseDistance.md};
`;

export const EmptyContainerStyled = styled.div`
	box-sizing: border-box;
	min-width: 0;
	max-width: 100%;
	min-height: 80px;
	padding: 0 ${baseDistance.sm};
	${FLEXCENTER};
	flex-wrap: wrap;
	font-size: ${fontSize.sm};
	letter-spacing: 0.01em;
	color: ${colorPalette.gray.c700Dark};
	text-align: center;
	white-space: normal;
	overflow-wrap: break-word;
`;
